import React from 'react'
import type { ProductSummary_ProductFragment } from '@generated/graphql'
import { useSession } from 'src/sdk/session'
import { useProductShelfSelectItemEvent } from 'src/sdk/analytics/hooks/useProductShelfSelectItemEvent'
import { useProductAbTestEvent } from 'src/sdk/analytics/hooks/useProductAbTestEvent'
import { isNewPDP } from 'src/utils/isNewPDP'
import { Image } from 'src/components/ui/Image'
import { Link } from 'gatsby'
import { getCookie } from 'src/utils/cookie'

export interface KitOrPDPProps {
  isKit: boolean
  linkProps: {
    to: string
    onClick: () => void
    'data-testid': string
  }
  name: string
  cardImage: string
  img: {
    url: string
    alternateName: string
  }
  aspectRatio: number
  shelfClass: string
  product: ProductSummary_ProductFragment
  isRelatedProducts?: boolean
}

const imgOptions = {
  sourceWidth: 1024,
  layout: 'constrained' as const,
  loading: 'lazy' as const,
  sizes: '(max-width: 768px) 25vw, 30vw',
  breakpoints: [360, 480, 720, 1024],
}

export default function KitOrPDPImage({
  isKit,
  linkProps,
  name,
  cardImage,
  img,
  aspectRatio,
  shelfClass,
  product,
  isRelatedProducts,
}: KitOrPDPProps) {
  const { person } = useSession()
  const { sendProductShelfSelectItem } = useProductShelfSelectItemEvent()
  const { sendProductAbTestEvent } = useProductAbTestEvent()
   const uuidToUse = getCookie('uuid')

  const sendSelectItemEventToAnalytics = () => {
    if (!product) {
      return
    }

    sendProductShelfSelectItem({
      item_id: product.id,
      item_name: product.name,
      item_collection:
        product.isVariantOf?.additionalProperty.find(
          (item) => item.name.toLowerCase() === 'coleção'.toLowerCase()
        )?.value ?? null,
      item_type: product?.name?.match(/^\p{L}+/u)?.[0] ?? null,
      item_reference: product.gtin,
      item_material:
        product.isVariantOf?.additionalProperty.find(
          (item) => item.name.toLowerCase() === 'material'.toLowerCase()
        )?.value ??
        product.isVariantOf?.additionalProperty.find(
          (item) => item.name.toLowerCase() === '1-material'.toLowerCase()
        )?.value ??
        product.isVariantOf?.additionalProperty.find(
          (item) =>
            item.name.toLowerCase() === 'material da pulseira'.toLowerCase()
        )?.value ??
        product.isVariantOf?.additionalProperty.find(
          (item) =>
            item.name.toLowerCase() === 'material da caixa'.toLowerCase()
        )?.value ??
        null,
      item_brand: product.brand?.brandName ?? null,
      item_price: product.offers?.offers[0]?.listPrice ?? null,
      item_currency: 'BRL',
      item_size:
        product.isVariantOf?.additionalProperty.find(
          (item) => item.name.toLowerCase() === 'tamanho'.toLowerCase()
        )?.value ??
        product.additionalProperty.find(
          (item) => item.name.toLowerCase() === 'aro'.toLowerCase()
        )?.value ??
        product.additionalProperty.find(
          (item) =>
            item.name.toLowerCase() === 'comprimento aprox'.toLowerCase()
        )?.value ??
        product.additionalProperty.find(
          (item) => item.name.toLowerCase() === 'altura aprox'.toLowerCase()
        )?.value ??
        product.additionalProperty.find(
          (item) => item.name.toLowerCase() === 'largura aprox'.toLowerCase()
        )?.value ??
        null,
      item_stone:
        product.isVariantOf?.additionalProperty.find(
          (item) => item.name.toLowerCase() === 'pedras'.toLowerCase()
        )?.value ??
        product.isVariantOf?.additionalProperty.find(
          (item) => item.name.toLowerCase() === 'pedra_valor'.toLowerCase()
        )?.value ??
        null,
      item_weight: product.name.match(/(\d+)\s*k/i)?.[0] ?? null,
      item_audience:
        product.isVariantOf?.additionalProperty.find(
          (item) => item.name.toLowerCase() === 'sugestão'.toLowerCase()
        )?.value ?? null,
      user_logged: !!person?.id,
      user_id: person?.id ?? null,
      uuid:uuidToUse
    })
  }

  const sendSelectItemAbTestEventToAnalytics = (productName: string) => {
    sendProductAbTestEvent({
      pdp_version: isNewPDP ? 'versao_2' : 'versao_1',
      action: 'click_product',
      component: 'produtos',
      position_interaction: 'meio',
      element_name: productName,
      section: 'produtos_relacionados',
      user_id: person?.id ?? null,
      user_logged: !!person?.id,
      uuid:uuidToUse
    })
  }

  const handleSaveProductIdInSessionStorage = () => {
    sessionStorage.setItem('clickedProductId', product.id)
  }

  return (
    <>
      {isKit ? (
        <a
          href={linkProps.to}
          onClick={() => {
            linkProps?.onClick()
            sendSelectItemEventToAnalytics()
            handleSaveProductIdInSessionStorage()

            if (isRelatedProducts) {
              sendSelectItemAbTestEventToAnalytics(name)
            }
          }}
          title={name}
          className={`${shelfClass}__name`}
        >
          <Image
            baseUrl={cardImage ?? img.url}
            alt={img.alternateName}
            aspectRatio={aspectRatio}
            {...imgOptions}
            imgStyle={{ objectFit: 'contain' }}
            backgroundColor="#fff"
          />
        </a>
      ) : (
        <Link
          {...linkProps}
          title={name}
          className={`${shelfClass}__name`}
          onClick={() => {
            sendSelectItemEventToAnalytics()
            handleSaveProductIdInSessionStorage()

            if (isRelatedProducts) {
              sendSelectItemAbTestEventToAnalytics(name)
            }
          }}
        >
          <Image
            baseUrl={cardImage ?? img.url}
            alt={img.alternateName}
            aspectRatio={aspectRatio}
            {...imgOptions}
            imgStyle={{ objectFit: 'contain' }}
            backgroundColor="#fff"
          />
        </Link>
      )}
    </>
  )
}
